import _ from 'underscore';
import Promise from 'bluebird';
import { useCallback, useState } from 'react';

import { BaseResultType } from '../types';

const DEBOUNCE_DELAY = 300;

export interface UseFetchingStateProps<ResultType> {
  fetchFn: (query: string) => Promise<{ results: ResultType[], totalCount: number } | ResultType[]>;
  fetchOnEmptyInput: boolean;
}

export type FetchingStatus = 'idle' | 'loading' | 'loaded';

export interface FetchingState<ResultType> {
  status: FetchingStatus;
  results: ResultType[];
}

export const useFetchingState = <ResultType extends BaseResultType>({
  fetchFn,
  fetchOnEmptyInput,
}: UseFetchingStateProps<ResultType>) => {
  const [state, setState] = useState<FetchingState<ResultType>>({ status: 'idle', results: [] });

  const fetch = useCallback((query) => {
    if (!query && !fetchOnEmptyInput) {
      setState({ status: 'idle', results: [] });
      return;
    }

    setState({ status: 'loading', results: [] });

    fetchFn(query)
      .then((response) => {
        const results = Array.isArray(response) ? response : response.results;
        setState({ status: 'loaded', results });
      })
      .catch(() => {
        setState({ status: 'loaded', results: [] });
      });
  }, [fetchFn, fetchOnEmptyInput, state.status]);

  const onCleanUp = useCallback(() => {
    setState({ status: 'idle', results: [] });
  }, []);

  const debouncedFetch = _.debounce(fetch, DEBOUNCE_DELAY);

  return {
    status: state.status,
    results: state.results,
    fetch: debouncedFetch,
    onCleanUp,
  };
};
